import { useReducer, useEffect, Reducer, useCallback } from 'react';
import { useAppShell } from '@ids/appshell/context';
import { NowApiResponse } from '@ids/types/now-api';

type Action<P> = { type: 'LOADING' } | { type: 'SUCCESS'; payload: P } | { type: 'ERROR' };

interface ResponseObject {
    error?: string;
    status?: number;
    statusText?: string;
}

interface State<P> {
    loading: boolean;
    data: (P & ResponseObject) | null;
    error: boolean;
}

function reducer<P>(state: State<P>, action: Action<P>): State<P> {
    switch (action.type) {
        case 'LOADING':
            return {
                ...state,
                loading: true,
            };
        case 'SUCCESS':
            return {
                ...state,
                loading: false,
                error: false,
                data: action.payload,
            };
        case 'ERROR':
            return {
                ...state,
                loading: false,
                error: true,
            };
        default:
            throw new Error(`Unknown action type`);
    }
}

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export type RequestParams = Omit<
    RequestInit,
    'method' | 'headers' | 'redirect' | 'referrerPolicy' | 'mode' | 'cache' | 'credentials'
>;

export const getRequestInit = (
    buildVersion: string,
    method: HTTPMethod = 'GET',
    requestParams: RequestParams = {}
): RequestInit => {
    return {
        method,
        ...requestParams,
        headers: new Headers({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'x-app-version': `1.0.0-${buildVersion}`,
            'x-app-type': 'bella',
            'x-device-type': 'phone',
            'x-client-user-agent': global.navigator ? window.navigator.userAgent : '',
        }),
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
    };
};

const parseCountry = (countryCode: string) => {
    const [first, last] = countryCode.split('-');

    return last ? last.toLocaleLowerCase() : first.toLocaleLowerCase();
};

const initialState = {
    loading: false,
    error: false,
    data: null,
};

type FetchOptions = {
    queryParams?: Record<string, string | number>;
    method?: HTTPMethod;
    init?: Omit<RequestInit, 'method'>;
    body?: BodyInit;
};

/**
 * **Managed State**
 *
 * If no url is passed to the useFetchAPI hook, the fetch will not run until it's called upon firing an action.
 *
 * @param {FetchOptions} options - The options you can pass to the underline fetch method, like method, headers,etc.
 * If no method is specified, GET is the default.
 *
 */
function useFetchAPI(
    url?: never,
    options?: FetchOptions,
    dependencies?: never
): {
    makeRequest: <ResponseData>(
        requestUrl: string,
        requestOptions?: Omit<FetchOptions, 'init'>
    ) => Promise<NowApiResponse<ResponseData>>;
};
/**
 * **Auto Managed State**
 *
 * If the url is passed to the useFetchAPI hook, the fetch will run on the componentDidMount life cycle.
 *
 * @param {string} url - The fetch url
 *
 * @param {FetchOptions} options - The options you can pass to the underline fetch method, like method, headers,etc.
 * If no method is specified, GET is the default.
 *
 * @param {unknown[]} dependencies - If the dependencies argument is passed it means that
 * fetch will onMount and again whenever any of the dependencies in the arry will change.
 *
 */
function useFetchAPI<P>(url: string, options?: FetchOptions, dependencies?: unknown[]): State<P>;
function useFetchAPI<P extends NowApiResponse<P>>(
    url?: string | never,
    { method = 'GET', init, queryParams, body }: FetchOptions = {},
    dependencies: never | unknown[] = []
) {
    const [{ loading, data, error }, dispatch] = useReducer<Reducer<State<P>, Action<P>>>(reducer, initialState);

    const {
        config: { shopApiBaseUrl, countryCode, buildVersion },
    } = useAppShell();

    const makeRequest = useCallback(
        async <ResponseData>(
            requestUrl: string,
            requestOptions: Omit<FetchOptions, 'init'> = { method: 'GET', queryParams: {} }
        ): Promise<NowApiResponse<ResponseData>> => {
            const requestInit = getRequestInit(buildVersion, requestOptions?.method, init);

            try {
                const response = await fetch(
                    `${shopApiBaseUrl}/${requestUrl}?${new URLSearchParams({
                        country: parseCountry(countryCode),
                        ...requestOptions?.queryParams,
                    })}`,
                    { ...requestInit, body: requestOptions.body }
                );

                const parsedData = await response.json();

                return parsedData;
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                return err;
            }
        },
        [buildVersion, countryCode, init, shopApiBaseUrl]
    );

    useEffect(() => {
        let isSubscribed = true;

        if (url && dependencies && isSubscribed) {
            const doFetch = async () => {
                dispatch({ type: 'LOADING' });

                const responseData = (await makeRequest(url, { method, queryParams, body })) as P;

                if (responseData.success) {
                    dispatch({ type: 'SUCCESS', payload: responseData });
                } else {
                    dispatch({ type: 'ERROR' });
                }
            };

            doFetch();
        }
        return () => {
            isSubscribed = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);

    return { loading, data, error, makeRequest };
}

export default useFetchAPI;
